import type { CMSLinkFields } from '@dobrozem/cms';
import escapeHTML from 'escape-html';
import type { FC, PropsWithChildren } from 'react';
import React, { Fragment } from 'react';
import { CMSLink } from '@/components/CMSLink';
import { Text } from '@/components/Text';
export type Node = {
  [key: string]: unknown;
  children?: Node[];
  newTab?: boolean;
  text?: string;
  type: string;
  url?: string;
  value?: {
    alt: string;
    url: string;
  };
};
export type CustomRenderers = {
  [key: string]: (args: {
    Serialize: SerializeFunction;
    children: React.ReactNode;
    index: number;
    node: Node;
  }) => JSX.Element; // eslint-disable-line
};
type SerializeFunction = React.FC<{
  DefaultRender?: FC<PropsWithChildren>;
  content?: Node[];
  customRenderText?: FC<{
    text: string;
  }>;
  customRenderers?: CustomRenderers;
}>;
const isText = (value: Record<string, unknown>): value is {
  text: string;
} => typeof value === 'object' && value !== null && typeof value.text === 'string';
export const Serialize: SerializeFunction = ({
  DefaultRender,
  content,
  customRenderText,
  customRenderers
}) => {
  return <Fragment>
      {content?.map((node, i) => {
      if (isText(node)) {
        let text = customRenderText ? customRenderText({
          text: node.text
        }) : <span dangerouslySetInnerHTML={{
          __html: escapeHTML(node.text)
        }} />;
        if (node.bold) {
          text = <strong key={i}>{text}</strong>;
        }
        if (node.code) {
          text = <code key={i}>{text}</code>;
        }
        if (node.italic) {
          text = <em key={i}>{text}</em>;
        }
        if (node.underline) {
          text = <span key={i} style={{
            textDecoration: 'underline'
          }}>
                {text}
              </span>;
        }
        if (node.strikethrough) {
          text = <span key={i} style={{
            textDecoration: 'line-through'
          }}>
                {text}
              </span>;
        }
        return <Fragment key={i}>{text}</Fragment>;
      }
      if (!node) {
        return null;
      }
      if (customRenderers && customRenderers[node.type] && typeof customRenderers[node.type] === 'function') {
        return customRenderers[node.type]({
          Serialize,
          children: <Serialize content={node.children} customRenderers={customRenderers} />,
          index: i,
          node
        });
      }
      switch (node.type) {
        case 'br':
          return <br key={i} />;
        case 'h1':
          return <Text key={i} tag='h1' type='h2'>
                <Serialize content={node.children} customRenderers={customRenderers} />
              </Text>;
        case 'h2':
          return <Text key={i} type='h2'>
                <Serialize content={node.children} customRenderers={customRenderers} />
              </Text>;
        case 'h3':
          return <Text key={i} type='h3'>
                <Serialize content={node.children} customRenderers={customRenderers} />
              </Text>;
        case 'h4':
          return <Text key={i} type='h4'>
                <Serialize content={node.children} customRenderers={customRenderers} />
              </Text>;
        case 'h5':
          return <h5 key={i}>
                <Serialize content={node.children} customRenderers={customRenderers} />
              </h5>;
        case 'h6':
          return <h6 key={i}>
                <Serialize content={node.children} customRenderers={customRenderers} />
              </h6>;
        case 'blockquote':
          return <Text key={i} tag='blockquote' type='h4'>
                <Serialize content={node.children} customRenderers={customRenderers} />
              </Text>;
        case 'ul':
          return <Text key={i} tag='ul' type='p3'>
                <Serialize content={node.children} customRenderers={customRenderers} />
              </Text>;
        case 'ol':
          return <ol key={i}>
                <Serialize content={node.children} customRenderers={customRenderers} />
              </ol>;
        case 'li':
          return <li key={i}>
                <Serialize content={node.children} customRenderers={customRenderers} />
              </li>;
        case 'link':
          return <CMSLink key={i} link={{
            doc: node.doc as CMSLinkFields['doc'],
            inNewTab: !!node.newTab,
            isCalculator: false,
            label: '',
            type: node.linkType === 'internal' ? 'link-doc' : 'link',
            url: escapeHTML(node.url)
          }}>
                <Serialize content={node.children} customRenderers={customRenderers} />
              </CMSLink>;
        default:
          return DefaultRender ? <DefaultRender key={i}>
                <Serialize content={node.children} customRenderText={customRenderText} customRenderers={customRenderers} />
              </DefaultRender> : <Text key={i} tag='p' type='p2'>
                <Serialize content={node.children} customRenderText={customRenderText} customRenderers={customRenderers} />
              </Text>;
      }
    })}
    </Fragment>;
};